import { useMemo, type ReactNode } from 'react';
import { styled } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import ShareOutlinedIcon from '@mui/icons-material/ShareOutlined';
import BlockIcon from '@mui/icons-material/Block';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import { DataGridPremium, GridColDef, GridRenderCellParams } from '@mui/x-data-grid-premium';
import { frFR } from '@mui/x-data-grid-premium/locales';
import { format } from 'date-fns/fp/format';
import { GqlTalentBoardEvent, TalentBoardPersistedEventType } from '@bloomays-lib/types.shared';
import { useNavigate } from 'react-router-dom';
import { useTalentBoardStore } from '../../stores/talentBoardStore';

const getEventTypeIcon = (type: TalentBoardPersistedEventType): ReactNode => {
  switch (type) {
    case TalentBoardPersistedEventType.TalentboardOpenJob:
      return <GroupsOutlinedIcon fontSize="small" />;
    case TalentBoardPersistedEventType.TalentboardOpenTalent:
      return <PersonOutlineOutlinedIcon fontSize="small" />;
    case TalentBoardPersistedEventType.TalentboardUpsertComment:
      return <ChatBubbleOutlineIcon fontSize="small" />;
    case TalentBoardPersistedEventType.TalentboardInvitationCreate:
      return <ShareOutlinedIcon fontSize="small" />;
    case TalentBoardPersistedEventType.TalentboardInvitationDelete:
      return <BlockIcon fontSize="small" />;
    case TalentBoardPersistedEventType.TalentboardTalentMarkedAsNotSeen:
      return <VisibilityOffOutlinedIcon fontSize="small" />;
    default:
      return null;
  }
};

const getEventTypeLabel = (type: TalentBoardPersistedEventType, t: TFunction): string => {
  switch (type) {
    case TalentBoardPersistedEventType.TalentboardOpenJob:
      return t('eventsDashboard.eventTypes.visit');
    case TalentBoardPersistedEventType.TalentboardOpenTalent:
      return t('eventsDashboard.eventTypes.viewTalent');
    case TalentBoardPersistedEventType.TalentboardUpsertComment:
      return t('eventsDashboard.eventTypes.comment');
    case TalentBoardPersistedEventType.TalentboardInvitationCreate:
      return t('eventsDashboard.eventTypes.share');
    case TalentBoardPersistedEventType.TalentboardInvitationDelete:
      return t('eventsDashboard.eventTypes.deleteInvitation');
    case TalentBoardPersistedEventType.TalentboardTalentMarkedAsNotSeen:
      return t('eventsDashboard.eventTypes.markAsNotSeen');
    default:
      return '';
  }
};

const getEventDescription = (event: GqlTalentBoardEvent, t: TFunction): string => {
  const data = event.data as Record<string, string> | null;
  const formattedDate = format("dd/MM/yyyy 'à' HH:mm")(new Date(event.date));

  switch (event.type) {
    case TalentBoardPersistedEventType.TalentboardOpenJob:
      return t('eventsDashboard.eventDescriptions.openJob', { formattedDate });
    case TalentBoardPersistedEventType.TalentboardOpenTalent:
      return t('eventsDashboard.eventDescriptions.openTalent', {
        talentFullName: data?.talentFullName ?? '',
        formattedDate,
      });
    case TalentBoardPersistedEventType.TalentboardUpsertComment:
      return t('eventsDashboard.eventDescriptions.upsertComment', {
        talentFullName: data?.talentFullName ?? '',
        formattedDate,
      });
    case TalentBoardPersistedEventType.TalentboardInvitationCreate:
      return t('eventsDashboard.eventDescriptions.invitationCreate', { email: data?.email ?? '', formattedDate });
    case TalentBoardPersistedEventType.TalentboardInvitationDelete:
      return t('eventsDashboard.eventDescriptions.invitationDelete', { email: data?.email ?? '', formattedDate });
    case TalentBoardPersistedEventType.TalentboardTalentMarkedAsNotSeen:
      return t('eventsDashboard.eventDescriptions.markedAsNotSeen', {
        talentFullName: data?.talentFullName ?? '',
        formattedDate,
      });
    default:
      return '';
  }
};

const EVENT_TYPES_WITH_TALENT_DATA = new Set<string>([
  TalentBoardPersistedEventType.TalentboardOpenTalent,
  TalentBoardPersistedEventType.TalentboardUpsertComment,
  TalentBoardPersistedEventType.TalentboardTalentMarkedAsNotSeen,
]);

// Le DataGrid necessite un valueGetter (string) pour le filtrage/tri et un renderCell (ReactNode) pour l'affichage.
// Cette fonction prend la description string generee par getEventDescription, localise le nom du talent dedans,
// et le remplace par un element cliquable qui navigue vers la page du talent.
// Le Tooltip affiche le texte complet au survol lorsque le texte est tronque.
const renderEventCell = (
  params: GridRenderCellParams,
  onTalentClick: (jobId: string, talentId: string) => void,
): ReactNode => {
  const event = params.row;
  const data = event.data;

  if (!EVENT_TYPES_WITH_TALENT_DATA.has(event.type) || !data?.talentId || !data?.talentFullName) {
    return (
      <Tooltip title={params.value ?? ''} enterDelay={300} enterNextDelay={100}>
        <span>{params.value}</span>
      </Tooltip>
    );
  }

  const description = String(params.value);
  const talentName = data.talentFullName;
  const parts = description.split(talentName);

  return (
    <Tooltip title={description} enterDelay={300} enterNextDelay={100}>
      <span>
        {parts[0]}
        <TalentLink onClick={() => onTalentClick(event.jobId, data.talentId)}>{talentName}</TalentLink>
        {parts.slice(1).join(talentName)}
      </span>
    </Tooltip>
  );
};

type EventsGridProps = {
  events: GqlTalentBoardEvent[];
  loading: boolean;
};

const EventsGrid = ({ events, loading }: EventsGridProps): ReactNode => {
  const { t } = useTranslation(['talentBoard']);
  const navigate = useNavigate();
  const { setNavState, setEventsModalOpen } = useTalentBoardStore();

  const handleTalentClick = (eventJobId: string, talentId: string) => {
    setEventsModalOpen(false);
    setNavState('talentboard');
    void navigate(`/talentboard/jobs/${eventJobId}/talent/${talentId}`);
  };

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'type',
        headerName: t('eventsDashboard.table.type'),
        width: 100,
        align: 'center',
        headerAlign: 'center',
        type: 'singleSelect',
        valueOptions: [
          t('eventsDashboard.eventTypes.visit'),
          t('eventsDashboard.eventTypes.viewTalent'),
          t('eventsDashboard.eventTypes.comment'),
          t('eventsDashboard.eventTypes.share'),
          t('eventsDashboard.eventTypes.deleteInvitation'),
          t('eventsDashboard.eventTypes.markAsNotSeen'),
        ],
        valueGetter: (_value: string, row: GqlTalentBoardEvent) => getEventTypeLabel(row.type, t),
        renderCell: (params) => getEventTypeIcon(params.row.type),
      },
      {
        field: 'date',
        headerName: t('eventsDashboard.table.date'),
        width: 150,
        type: 'dateTime',
        valueGetter: (_value: string, row: GqlTalentBoardEvent) => new Date(row.date),
        valueFormatter: (value: Date) => format('dd/MM/yyyy')(value),
      },
      {
        field: 'authorEmail',
        headerName: t('eventsDashboard.table.who'),
        width: 280,
        type: 'string',
      },
      {
        field: 'event',
        headerName: t('eventsDashboard.table.event'),
        flex: 1,
        minWidth: 300,
        type: 'string',
        valueGetter: (_value: string, row: GqlTalentBoardEvent) => getEventDescription(row, t),
        renderCell: (params) => renderEventCell(params, handleTalentClick),
      },
    ],
    [t, handleTalentClick],
  );

  return (
    <>
      <ContentTitle variant="heading6">{t('eventsDashboard.title')}</ContentTitle>
      <DataGridContainer>
        <DataGridPremium
          rows={events}
          columns={columns}
          getRowId={(row: GqlTalentBoardEvent) => row.id}
          pagination
          pageSizeOptions={[10, 25, 50]}
          initialState={{
            pagination: { paginationModel: { pageSize: 10 } },
            sorting: { sortModel: [{ field: 'date', sort: 'desc' }] },
          }}
          localeText={frFR.components.MuiDataGrid.defaultProps.localeText}
          loading={loading}
          density="standard"
          disableRowSelectionOnClick
          showToolbar
          sx={{
            '& .MuiDataGrid-cell[data-field="type"]': {
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            },
          }}
        />
      </DataGridContainer>
    </>
  );
};

const ContentTitle = styled(Typography)(() => ({
  marginBottom: '24px',
}));

const DataGridContainer = styled('div')(() => ({
  height: 'calc(100% - 60px)',
  width: '100%',
}));

const TalentLink = styled('span')(({ theme }) => ({
  color: theme.palette.neutral.black,
  fontWeight: 600,
  textDecoration: 'underline',
  cursor: 'pointer',
  '&:hover': {
    opacity: 0.7,
  },
}));

export default EventsGrid;
